import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "../../hooks/useAuth";
import {
  add_support_tickets_api,
  get_ticket_detail_api,
  update_support_tickets_api,
} from "../../DAL/SupportTickets";
import { useSnackbar } from "notistack";
import { useContent } from "../../hooks/useContent";
import { useNavigate, useParams } from "react-router-dom";
import FormButtons from "../../Components/GeneralComponents/FormButtons";
import PageLoader from "../../Components/GeneralComponents/PageLoader";

const CreateOrUpdateTicket = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const { ticket_id } = useParams();
  const { setPageHeading } = useContent();
  const [inputs, setInputs] = useState({
    ticket_title: "",
    ticket_description: "",
    Category: "",
    status: "OPEN",
    priority: "MEDIUM",
    assignee: "CF ADMIN",
    customername: userInfo.partner_username || "",
    contact_first_name: userInfo.first_name,
    contact_mid_name: "",
    contact_last_name: userInfo.last_name,
    contact_email: userInfo.email,
    contact_phone: userInfo.phone_mobile,
    newupdate: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState("");
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const getTicketDetail = async () => {
    const result = await get_ticket_detail_api(ticket_id);
    if (result.code === 200) {
      setInputs((old) => ({ ...old, ...result.ticket }));
      // Set lastUpdate from ticket's updated_at field
      if (result.ticket.updated_at) {
        setLastUpdate(
          new Date(result.ticket.updated_at)
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
        );
      } else if (result.ticket.created_at) {
        setLastUpdate(
          new Date(result.ticket.created_at)
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
        );
      }
    } else {
      enqueueSnackbar(`${result.message}`, { variant: "error" });
    }
    setIsLoadingPage(false);
  };

  useEffect(() => {
    setPageHeading(`${ticket_id ? "Update" : "Create"}  Support Ticket`);
    if (ticket_id) {
      getTicketDetail();
    } else {
      setIsLoadingPage(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run when route id changes
  }, [ticket_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!inputs.ticket_title?.trim())
      newErrors.ticket_title = "Ticket Title is required.";
    if (!inputs.ticket_description?.trim())
      newErrors.ticket_description = "Description is required.";
    if (!inputs.contact_email?.trim())
      newErrors.contact_email = "Email is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    const result = ticket_id
      ? await update_support_tickets_api(inputs, ticket_id)
      : await add_support_tickets_api(inputs);
    if (result.code === 200) {
      enqueueSnackbar(
        `Ticket ${ticket_id ? "updated" : "created"} successfully.`,
        { variant: "success" }
      );
      handleCancel();
    } else {
      enqueueSnackbar(`${result.message}`, { variant: "error" });
    }
    setIsLoading(false);
  };

  const handleCancel = () => navigate("/support-tickets");

  if (isLoadingPage) return <PageLoader />;

  return (
    <div className="login-page container">
      <div className="d-flex justify-content-center">
        <div className="container mx-auto">
          <div className="form-wrapper">
            <form onSubmit={handleSubmit}>
              <div className="d-flex justify-content-end align-items-center mb-4">
                <FormButtons {...{ isLoading, handleCancel }} />
              </div>
              {ticket_id && (
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Ticket Number</label>
                      <div className="input-wrapper">
                        <span className="input-icon">#</span>
                        <input
                          type="text"
                          value={ticket_id}
                          readOnly
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Status</label>
                      <div className="input-wrapper">
                        <Icon icon="mdi:progress-clock" className="input-icon" />
                        <select
                          name="status"
                          value={inputs.status}
                          className="form-control"
                          disabled
                        >
                          {[
                            "OPEN",
                            "CLOSED",
                            "IN-PROGRESS",
                            "WAITING FOR DETAIL",
                            "COMPLETED",
                          ].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Last Update</label>
                      <div className="input-wrapper">
                        <Icon icon="mdi:calendar-clock" className="input-icon" />
                        <input
                          type="text"
                          value={lastUpdate}
                          readOnly
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="row mb-3">
                <div className="col-md-8">
                  <div className="form-group">
                    <label>Ticket Title *</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:format-title" className="input-icon" />
                      <input
                        type="text"
                        name="ticket_title"
                        value={inputs.ticket_title}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.ticket_title ? "is-invalid" : ""
                        }`}
                        placeholder=""
                      />
                    </div>
                    {errors.ticket_title && (
                      <div className="text-danger small mt-1">
                        {errors.ticket_title}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Priority</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:alert-circle" className="input-icon" />
                      <select
                        name="priority"
                        value={inputs.priority}
                        onChange={handleChange}
                        className="form-control"
                      >
                        {["LOW", "MEDIUM", "HIGH"].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group mb-3">
                <label>Description *</label>
                <div className="input-wrapper">
                  <textarea
                    name="ticket_description"
                    rows="4"
                    value={inputs.ticket_description}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.ticket_description ? "is-invalid" : ""
                    }`}
                  ></textarea>
                </div>
                {errors.ticket_description && (
                  <div className="text-danger small mt-1">
                    {errors.ticket_description}
                  </div>
                )}
              </div>
              {/* <div className="row mb-3">
                                <div className="col-md-6 form-group">
                                    <label>Category</label>
                                    <div className="input-wrapper">
                                        <Icon icon="mdi:folder-outline" className="input-icon" />
                                        <select
                                            name="Category"
                                            value={inputs.Category}
                                            onChange={handleChange}
                                            className="form-control"
                                        >
                                            <option value="">Select Category</option>
                                            {["WEB", "iOS APP", "ANDROID APP", "ADMIN PANEL", "OTHER"].map((cat) => (
                                                <option key={cat} value={cat}>
                                                    {cat}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6 form-group">
                                    <label>Assignee</label>
                                    <div className="input-wrapper">
                                        <Icon icon="mdi:account-tie" className="input-icon" />
                                        <input type="text" name="assignee" value={inputs.assignee} readOnly className="form-control" />
                                    </div>
                                </div>
                            </div> */}
              <h4 className="mt-4 mb-2 middle-heading">Customer Info</h4>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>First Name</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:account-edit" className="input-icon" />
                      <input
                        type="text"
                        name="contact_first_name"
                        value={inputs.contact_first_name}
                        onChange={handleChange}
                        className="form-control"
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label>Last Name</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:account-outline" className="input-icon" />
                      <input
                        type="text"
                        name="contact_last_name"
                        value={inputs.contact_last_name}
                        onChange={handleChange}
                        className="form-control"
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Email *</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:email-outline" className="input-icon" />
                      <input
                        type="email"
                        name="contact_email"
                        value={inputs.contact_email}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.contact_email ? "is-invalid" : ""
                        }`}
                        disabled
                      />
                    </div>
                    {errors.contact_email && (
                      <div className="text-danger small mt-1">
                        {errors.contact_email}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Phone</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:phone-outline" className="input-icon" />
                      <input
                        type="text"
                        name="contact_phone"
                        value={inputs.contact_phone}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {ticket_id && (
                <div className="form-group mb-3">
                  <label>Provide New Update</label>
                  <div className="input-wrapper">
                    <textarea
                      name="newupdate"
                      rows="3"
                      value={inputs.newupdate}
                      onChange={handleChange}
                      className="form-control"
                    ></textarea>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end align-items-center mb-4">
                <FormButtons {...{ isLoading, handleCancel }} />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrUpdateTicket;
